import {
  Button,
  Flex,
  FormControl,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import {
  suppliersApi,
  SupplierProductGroupDto,
} from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  productGroupsApi,
  ProductGroup,
} from '@texas/api/endpoints/metadata/productGroupsApi';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import {
  parseOptionalLeadTime,
  validateOptionalLeadTime,
} from './leadTimeUtils';

interface ProductGroupFormRow {
  productGroupId: number;
  value: string;
  code: string;
  sampleLeadTime: string;
  bulkLeadTime: string;
  shipmentLeadTime: string;
}

interface FormValues {
  productGroups: ProductGroupFormRow[];
}

interface Props extends SharedDisclosureProps {
  supplierId: number;
  existingProductGroupIds: number[];
  onAdded: (updated: SupplierProductGroupDto[]) => void;
}

export function AddProductGroupsModal({
  supplierId,
  existingProductGroupIds,
  isOpen,
  onClose,
  onAdded,
}: Props) {
  const { t } = useTranslation();

  const {
    data: allProductGroups,
    refetch: loadProductGroups,
    loading,
  } = useApiResource(productGroupsApi.getProductGroups);

  const [search, setSearch] = useState('');

  const { request: bulkAddRequest, error } = useApiRequest(
    suppliersApi.productGroups.bulkAdd,
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { productGroups: [] } });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productGroups',
  });

  useEffect(() => {
    if (isOpen) {
      reset({ productGroups: [] });
      loadProductGroups();
      setSearch('');
    }
  }, [isOpen, loadProductGroups, reset]);

  const availableProductGroups = (allProductGroups ?? []).filter(
    (pg) =>
      !existingProductGroupIds.includes(pg.id) &&
      !pg.archived &&
      !fields.some((f) => f.productGroupId === pg.id) &&
      (search.trim() === '' ||
        pg.value.toLowerCase().includes(search.toLowerCase()) ||
        pg.code.toLowerCase().includes(search.toLowerCase())),
  );

  const toggleProductGroup = (pg: ProductGroup) => {
    const existingIndex = fields.findIndex((f) => f.productGroupId === pg.id);
    if (existingIndex >= 0) {
      remove(existingIndex);
    } else {
      append({
        productGroupId: pg.id,
        value: pg.value,
        code: pg.code,
        sampleLeadTime: '',
        bulkLeadTime: '',
        shipmentLeadTime: '',
      });
    }
  };

  const onSubmit = async (data: FormValues) => {
    await request(
      bulkAddRequest,
      [
        supplierId,
        {
          productGroups: data.productGroups.map((r) => ({
            productGroupId: r.productGroupId,
            sampleLeadTime: parseOptionalLeadTime(r.sampleLeadTime),
            bulkLeadTime: parseOptionalLeadTime(r.bulkLeadTime),
            shipmentLeadTime: parseOptionalLeadTime(r.shipmentLeadTime),
          })),
        },
      ],
      (response) => {
        reset({ productGroups: [] });
        onAdded(response);
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset({ productGroups: [] });
        onClose();
      }}
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('suppliers.addProductGroups')}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            {loading && <SpinnerWithLabel label={t('general.loadingData')} />}
            <Text mb={3} color="gray.300" fontSize="sm">
              {t('suppliers.addProductGroupsDesc')}
            </Text>

            <Input
              variant="texas"
              size="sm"
              mb={3}
              placeholder={t('general.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus={true}
            />

            {availableProductGroups.length > 0 && (
              <Flex wrap="wrap" gap={2} mb={4}>
                {availableProductGroups.map((pg) => (
                  <Button
                    key={pg.id}
                    size="sm"
                    variant="texas-light"
                    onClick={() => toggleProductGroup(pg)}
                  >
                    {pg.value} ({pg.code})
                  </Button>
                ))}
              </Flex>
            )}

            {fields.length > 0 && (
              <Flex direction="column" gap={3}>
                {fields.map((field, index) => (
                  <Flex
                    key={field.id}
                    gap={3}
                    align="flex-start"
                    p={3}
                    borderRadius="md"
                    bg="whiteAlpha.50"
                    _light={{ bg: 'blackAlpha.50' }}
                  >
                    <FormControl flex={1}>
                      <Text fontWeight="medium" minW="120px" py={1}>
                        {field.value} ({field.code})
                      </Text>
                    </FormControl>
                    <TexasInputWrapper
                      label={t('suppliers.sampleLeadTime')}
                      rightAddon={t('inquiry.days')}
                      error={errors.productGroups?.[index]?.sampleLeadTime}
                      formControlProps={{ flex: 1 }}
                      inputGroupProps={{ size: 'sm' }}
                    >
                      <Input
                        type="number"
                        variant="texas"
                        {...register(
                          `productGroups.${index}.sampleLeadTime` as const,
                          {
                            validate: (v) => validateOptionalLeadTime(v, t),
                          },
                        )}
                      />
                    </TexasInputWrapper>
                    <TexasInputWrapper
                      label={t('suppliers.bulkLeadTime')}
                      rightAddon={t('inquiry.days')}
                      error={errors.productGroups?.[index]?.bulkLeadTime}
                      formControlProps={{ flex: 1 }}
                      inputGroupProps={{ size: 'sm' }}
                    >
                      <Input
                        type="number"
                        variant="texas"
                        {...register(
                          `productGroups.${index}.bulkLeadTime` as const,
                          {
                            validate: (v) => validateOptionalLeadTime(v, t),
                          },
                        )}
                      />
                    </TexasInputWrapper>
                    <TexasInputWrapper
                      label={t('suppliers.shipmentLeadTime')}
                      rightAddon={t('inquiry.days')}
                      error={errors.productGroups?.[index]?.shipmentLeadTime}
                      formControlProps={{ flex: 1 }}
                      inputGroupProps={{ size: 'sm' }}
                    >
                      <Input
                        type="number"
                        variant="texas"
                        {...register(
                          `productGroups.${index}.shipmentLeadTime` as const,
                          {
                            validate: (v) => validateOptionalLeadTime(v, t),
                          },
                        )}
                      />
                    </TexasInputWrapper>
                    <IconButton
                      aria-label={t('general.remove')}
                      icon={<Icons.Close boxSize={defaultIconSize} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => remove(index)}
                    />
                  </Flex>
                ))}
              </Flex>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="texas-light" onClick={onClose}>
              {t('general.cancel')}
            </Button>
            <Button
              variant="texas-solid"
              type="submit"
              isDisabled={fields.length === 0}
              isLoading={isSubmitting}
            >
              {t('general.add')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
